import { Col } from "antd";
import React, { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { PropertyType, useGetPropertyTypes } from "../../../../../../api/v1";
import { SelectOption } from "../../../../../../types/select";
import { ReactSelectField } from "../../../../../atoms/FormElement";
import { getSelectPlaceholderMessage, getValidationProps } from "../../../../../libs/form";
import { convertToTitleCase } from "../../../../../libs/utils";

const getUsageTypeOptions = (data: PropertyType[]): SelectOption[] => {
  return data.map((item) => ({ label: convertToTitleCase(item.usagesType), value: item.usagesType }));
};

const getUnitTypeOptions = (data: PropertyType[], usageType: string): SelectOption[] => {
  const found = data.find((item) => item.usagesType === usageType);
  if (!found) return [];

  return Object.keys(found.unitType).map((type) => ({ label: convertToTitleCase(type), value: type }));
};

const formatUnitSubType = (subType: string): string => {
  // Split into tokens while preserving symbols
  const tokens = subType.split(/(\s+|\+|-|\(|\)|\/)/g);

  const formattedTokens = tokens.map((token) => {
    // Tokens with digits should remain unchanged (like 2BR, 5BR+M)
    if (/\d/.test(token)) return token;

    // Tokens that are pure symbols/spaces remain unchanged
    if (/^\s*$|^[()+\-\/]$/.test(token)) return token;

    // Otherwise title case the text portion
    return convertToTitleCase(token);
  });

  return formattedTokens.join("");
};

const getUnitSubTypeOptions = (data: PropertyType[], usageType: string, unitType: string): SelectOption[] => {
  const found = data.find((item) => item.usagesType === usageType);
  if (!found) return [];

  const subTypes = found.unitType[unitType];
  if (!subTypes) return [];

  return subTypes.map((subType) => ({ label: formatUnitSubType(subType), value: formatUnitSubType(subType) }));
};

type UnitTypeFieldsProps = {
  colSpan?: number;
};

export const UnitTypeFields = (props: UnitTypeFieldsProps) => {
  const { colSpan } = props;

  const { control, formState, setValue } = useFormContext();
  const { errors } = formState;

  const watchedUsageType: SelectOption = useWatch({ name: "usagesType", control });
  const watchedUnitType: SelectOption = useWatch({ name: "unitType", control });

  const { propertyTypes, isLoadingPropertyTypes } = useGetPropertyTypes();

  useEffect(() => {
    setValue("unitType", null);
    setValue("unitSubType", null);
  }, [watchedUsageType]);

  useEffect(() => {
    setValue("unitSubType", null);
  }, [watchedUnitType]);

  return (
    <>
      <Col span={colSpan || 6}>
        <ReactSelectField
          label="Usage Type"
          fieldname="usagesType"
          control={control}
          selectOption={getUsageTypeOptions(propertyTypes)}
          {...getValidationProps({ fieldName: "usagesType", errors, isRequired: true })}
          iProps={{
            placeholder: getSelectPlaceholderMessage("usagesType"),
            isLoading: isLoadingPropertyTypes,
            isDisabled: isLoadingPropertyTypes,
          }}
        />
      </Col>

      <Col span={colSpan || 6}>
        <ReactSelectField
          label="Unit Type"
          fieldname="unitType"
          control={control}
          selectOption={getUnitTypeOptions(propertyTypes, watchedUsageType?.value)}
          {...getValidationProps({ fieldName: "unitType", errors, isRequired: true })}
          iProps={{
            placeholder: getSelectPlaceholderMessage("unitType"),
            isDisabled: !watchedUsageType,
          }}
        />
      </Col>

      <Col span={colSpan || 6}>
        <ReactSelectField
          isRequired
          label="Unit Sub Type"
          fieldname="unitSubType"
          control={control}
          selectOption={getUnitSubTypeOptions(propertyTypes, watchedUsageType?.value, watchedUnitType?.value)}
          {...getValidationProps({ fieldName: "unitSubType", errors, isRequired: true })}
          iProps={{
            placeholder: getSelectPlaceholderMessage("unitSubType"),
            isDisabled: !watchedUnitType,
          }}
        />
      </Col>
    </>
  );
};
